package top.wcpe.allincore.dsl.compiler.codegen

import top.wcpe.allincore.dsl.compiler.ast.CanvasAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.codegen.internal.ExprSerializer
import top.wcpe.allincore.dsl.ir.model.UiCanvasData

/**
 * 将 [CanvasAst] 编译为 [UiCanvasData]（M11.6 / M11.7 Stage 1）。
 *
 * - width/height/duration：若 AST 节点是数值常量（NumberLit 或 `-NumberLit`），
 *   走常量字段路径并在编译期做范围校验；否则编译为 ExprBlob，
 *   常量字段填占位值（0/1000）。
 * - durationMs / loopMode 缺省时采用 [UiCanvasData] 默认值。
 * - onceBlock 为 M11.6 预留字段；IR 端尚未提供 once blob 载荷，此处暂不序列化。
 */
class CanvasEmitter(
    private val drawBlobEmitter: DrawBlobEmitter = DrawBlobEmitter(),
) {

    fun emit(canvas: CanvasAst): UiCanvasData {
        val drawBlob = drawBlobEmitter.emit(canvas.drawBlock)

        // width/height/duration：能折叠为编译期常量则走常量路径，否则编译为 ExprBlob，
        // 常量字段填占位值（0 / DEFAULT_DURATION_MS）。
        val w = foldIntField(canvas.width, "canvas width")
        val h = foldIntField(canvas.height, "canvas height")
        val d = canvas.durationMs?.let { foldDoubleField(it, "canvas duration") }
            ?: FoldedDouble(UiCanvasData.DEFAULT_DURATION_MS, null)

        return UiCanvasData(
            w.value,
            h.value,
            drawBlob,
            d.value,
            canvas.loopMode ?: UiCanvasData.DEFAULT_LOOP_MODE,
            w.expr,
            h.expr,
            d.expr,
        )
    }

    /** 折叠结果：常量值 + 表达式 blob（互斥，expr 非 null 时 value 为占位 0）。 */
    private data class FoldedInt(val value: Int, val expr: ByteArray?)

    /** 折叠结果：常量值 + 表达式 blob（互斥，expr 非 null 时 value 为 DEFAULT_DURATION_MS）。 */
    private data class FoldedDouble(val value: Double, val expr: ByteArray?)

    /** 折叠 Int 字段：常量则走常量路径，否则序列化为 ExprBlob 并以 0 占位。 */
    private fun foldIntField(expr: ExprAst, field: String): FoldedInt {
        val folded = foldNonNegativeInt(expr, field)
        return if (folded != null) FoldedInt(folded, null)
        else FoldedInt(0, ExprSerializer.serializeExpr(expr))
    }

    /** 折叠 Double 字段：常量则走常量路径，否则序列化为 ExprBlob 并以 DEFAULT_DURATION_MS 占位。 */
    private fun foldDoubleField(expr: ExprAst, field: String): FoldedDouble {
        val folded = foldNonNegativeDouble(expr, field)
        return if (folded != null) FoldedDouble(folded, null)
        else FoldedDouble(UiCanvasData.DEFAULT_DURATION_MS, ExprSerializer.serializeExpr(expr))
    }

    /**
     * 把表达式尝试折叠为非负整数常量；失败返回 null（表示走表达式路径）。
     *
     * 仅识别 NumberLit 与 `UnaryOp("-", NumberLit)`，其它形式（含 props.x、运算）
     * 全部留给运行时求值。若识别为常量但越界（负数 / 非整数 / 超出 u16），
     * 抛 IllegalStateException 让 codegen 阶段早失败，与历史 parser 错误等价。
     */
    private fun foldNonNegativeInt(expr: ExprAst, field: String): Int? {
        val numeric = tryFoldNumeric(expr) ?: return null
        if (numeric != numeric.toInt().toDouble()) {
            error("$field 必须是整数，实际为 $numeric (位置 ${expr.position})")
        }
        val intValue = numeric.toInt()
        if (intValue < 0) {
            error("$field 不能为负数 (位置 ${expr.position})")
        }
        return intValue
    }

    /** 把表达式尝试折叠为非负 double 常量；规则同 [foldNonNegativeInt]。 */
    private fun foldNonNegativeDouble(expr: ExprAst, field: String): Double? {
        val numeric = tryFoldNumeric(expr) ?: return null
        if (numeric < 0.0) {
            error("$field 不能为负数 (位置 ${expr.position})")
        }
        return numeric
    }

    /** 仅识别 NumberLit 或 `-NumberLit` 作为编译期数值常量。 */
    private fun tryFoldNumeric(expr: ExprAst): Double? = when (expr) {
        is ExprAst.Literal.NumberLit -> expr.value.toDouble()
        is ExprAst.UnaryOp -> if (expr.op == "-" && expr.operand is ExprAst.Literal.NumberLit) {
            -(expr.operand.value.toDouble())
        } else null
        else -> null
    }
}
